import { type CSSProperties, type ReactNode } from 'react';
import { Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { colors } from '@tempmee/tailwind';
import { Column } from '../Column';

interface IGridTableProps<T = unknown> {
  /** Array of data items to render in the table */
  data: T[];
  /** Function to render each row - receives item and returns ReactNode */
  renderRow: (item: T) => ReactNode;
  /** Array of column header titles */
  headerTitles: string[];
  /** Loading state - shows spinner when true */
  isLoading?: boolean;
  /** Additional styles for the header row */
  headerStyle?: CSSProperties;
}

/**
 * GridTable Component
 *
 * A reusable table component that renders data in a grid layout with dynamic headers,
 * custom row rendering, and optional loading state with spinner.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for office job invites display. Consider whether
 * this should be moved to a more specific location like:
 * - components/ProfessionalsHub/components/shared
 * - components/shared/Tables
 *
 * If this is truly only used for office job invites context, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <GridTable
 *   data={jobs}
 *   renderRow={(item) => <CustomCard job={item} />}
 *   headerTitles={['Date', 'Profession', 'Time']}
 *   isLoading={false}
 *   headerStyle={{ marginLeft: 80 }}
 * />
 * ```
 */
export const GridTable = <T,>({
  data,
  renderRow,
  headerTitles,
  isLoading = false,
  headerStyle = {},
}: IGridTableProps<T>) => (
  <Column>
    <div
      style={{
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${headerTitles.length}, 2fr) 1fr`,
          ...headerStyle,
        }}
      >
        {headerTitles?.map((title) => (
          <Typography
            as="span"
            bold
            color={colors.primary[500]}
            variant="body1"
            key={title}
            textAlign="center"
            style={{ paddingBottom: 8 }}
          >
            {title}
          </Typography>
        ))}
        <Typography as="span" style={{ cursor: 'default', opacity: 0 }}>
          Action
        </Typography>
      </div>
      <Column gap={8} marginTop={12}>
        {data.map((item, index) => (
          <div key={index}>{renderRow(item)}</div>
        ))}
      </Column>
    </div>

    {isLoading && (
      <div style={{ marginTop: 20 }}>
        <CenteredSpinner />
      </div>
    )}
  </Column>
);
